import type { McpTool } from "@p0security/shared/types/agentic/mcp";

/** A {@link McpTool} tagged with the GCP IAM permission(s) it exercises. An
 * empty array means confirmed to need none; absent means unclassified. */
type GcpIamMcpTool = McpTool & { iamPermissions?: string[] };

export const gcpIamMcpTools: GcpIamMcpTool[] = [
  {
    name: "list_service_accounts",
    description: "List service accounts in a project.",
    iamPermissions: ["iam.serviceAccounts.list"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        pageSize: {
          type: "integer",
          description: "Max results per page (default 20, max 100)",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
      },
      required: ["project"],
    },
  },
  {
    name: "get_service_account",
    description: "Get a service account.",
    iamPermissions: ["iam.serviceAccounts.get"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "create_service_account",
    description:
      "Create a service account. Body (CreateServiceAccountRequest): { accountId: string, serviceAccount?: { displayName?: string, description?: string } }.",
    iamPermissions: ["iam.serviceAccounts.create"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project"],
    },
  },
  {
    name: "patch_service_account",
    description:
      "Update a service account's displayName/description. Body (PatchServiceAccountRequest): { serviceAccount: { displayName?: string, description?: string }, updateMask: string }.",
    // Catalog scores this risk-free: only allows updating description/name.
    iamPermissions: ["iam.serviceAccounts.update"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "update_service_account",
    description:
      "[Deprecated, use patch_service_account] Replace a service account. Body is a full ServiceAccount resource.",
    // Same underlying permission as patch_service_account (see above).
    iamPermissions: ["iam.serviceAccounts.update"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "delete_service_account",
    description: "Delete a service account.",
    iamPermissions: ["iam.serviceAccounts.delete"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "undelete_service_account",
    description:
      "Restore a recently deleted service account. The serviceAccount parameter here only accepts the numeric uniqueId, not the email address.",
    iamPermissions: ["iam.serviceAccounts.undelete"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account's unique numeric ID (the email address is not accepted here)",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "disable_service_account",
    description: "Disable a service account.",
    iamPermissions: ["iam.serviceAccounts.disable"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "enable_service_account",
    description: "Enable a previously disabled service account.",
    iamPermissions: ["iam.serviceAccounts.enable"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "get_service_account_iam_policy",
    description: "Get the IAM policy attached to a service account.",
    iamPermissions: ["iam.serviceAccounts.getIamPolicy"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        requestedPolicyVersion: {
          type: "integer",
          description: "Max policy version to format the policy as: 0, 1, or 3",
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "set_service_account_iam_policy",
    description:
      "Set the IAM policy attached to a service account. Body (SetIamPolicyRequest): { policy: { bindings: [...], etag?: string }, updateMask?: string }.",
    // Catalog scores this CRITICAL unconditionally, regardless of the role
    // granted — the capability to modify IAM policy is itself the risk.
    iamPermissions: ["iam.serviceAccounts.setIamPolicy"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "test_service_account_iam_permissions",
    description:
      "Check which of the given permissions the caller has on a service account. Body (TestIamPermissionsRequest): { permissions: string[] }.",
    // Introspection-only: GCP requires no specific permission to call this.
    iamPermissions: [],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "list_service_account_keys",
    description: "List keys for a service account.",
    // Catalog scores this risk-free: only shows public keys.
    iamPermissions: ["iam.serviceAccountKeys.list"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        keyTypes: {
          type: "array",
          description:
            "Filter by key type(s); if omitted, all keys are returned",
          items: {
            type: "string",
            enum: ["KEY_TYPE_UNSPECIFIED", "USER_MANAGED", "SYSTEM_MANAGED"],
          },
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "get_service_account_key",
    description: "Get a service account key.",
    // Same as list_service_account_keys above: risk-free (public keys only).
    iamPermissions: ["iam.serviceAccountKeys.get"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        key: {
          type: "string",
          description: "Service account key ID",
        },
        publicKeyType: {
          type: "string",
          description:
            "Public key format to return (default TYPE_NONE, i.e. no public key)",
          enum: ["TYPE_NONE", "TYPE_X509_PEM_FILE", "TYPE_RAW_PUBLIC_KEY"],
        },
      },
      required: ["project", "serviceAccount", "key"],
    },
  },
  {
    name: "delete_service_account_key",
    description: "Delete a service account key.",
    iamPermissions: ["iam.serviceAccountKeys.delete"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        key: {
          type: "string",
          description: "Service account key ID",
        },
      },
      required: ["project", "serviceAccount", "key"],
    },
  },
  {
    name: "upload_service_account_key",
    description:
      "Upload a public key to create a new externally-managed key. Body (UploadServiceAccountKeyRequest): { publicKeyData: string (base64-encoded PEM certificate text, including the -----BEGIN CERTIFICATE-----/-----END CERTIFICATE----- lines — not raw DER) }.",
    // Same GCP permission as generating a key (iam.serviceAccountKeys.create).
    iamPermissions: ["iam.serviceAccountKeys.create"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount"],
    },
  },
  {
    name: "disable_service_account_key",
    description: "Disable a service account key.",
    iamPermissions: ["iam.serviceAccountKeys.disable"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        key: {
          type: "string",
          description: "Service account key ID",
        },
        body: {
          type: "object",
          description: "DisableServiceAccountKeyRequest body.",
          properties: {
            serviceAccountKeyDisableReason: {
              type: "string",
              description:
                "Why the key is being disabled; defaults to USER_INITIATED when omitted",
              enum: [
                "SERVICE_ACCOUNT_KEY_DISABLE_REASON_UNSPECIFIED",
                "SERVICE_ACCOUNT_KEY_DISABLE_REASON_USER_INITIATED",
                "SERVICE_ACCOUNT_KEY_DISABLE_REASON_EXPOSED",
                "SERVICE_ACCOUNT_KEY_DISABLE_REASON_COMPROMISE_DETECTED",
              ],
            },
          },
          additionalProperties: false,
        },
      },
      required: ["project", "serviceAccount", "key"],
    },
  },
  {
    name: "enable_service_account_key",
    description: "Enable a previously disabled service account key.",
    iamPermissions: ["iam.serviceAccountKeys.enable"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        serviceAccount: {
          type: "string",
          description:
            "Service account email address (e.g. my-sa@project.iam.gserviceaccount.com) or unique numeric ID",
        },
        key: {
          type: "string",
          description: "Service account key ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "serviceAccount", "key"],
    },
  },
  {
    name: "list_custom_roles",
    description: "List custom roles defined in a project.",
    iamPermissions: ["iam.roles.list"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        pageSize: {
          type: "integer",
          description: "Max results per page (default 300, max 1000)",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        showDeleted: {
          type: "boolean",
          description: "Include deleted roles",
        },
        view: {
          type: "string",
          description: "BASIC (default, omits includedPermissions) or FULL",
          enum: ["BASIC", "FULL"],
        },
      },
      required: ["project"],
    },
  },
  {
    name: "get_custom_role",
    description: "Get a custom role.",
    iamPermissions: ["iam.roles.get"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        role: {
          type: "string",
          description:
            "Custom role ID, the last path segment (e.g. myCustomRole)",
        },
      },
      required: ["project", "role"],
    },
  },
  {
    name: "create_custom_role",
    description:
      "Create a custom role in a project. Body (CreateRoleRequest): { roleId: string, role?: { title?: string, description?: string, includedPermissions?: string[], stage?: string } }.",
    iamPermissions: ["iam.roles.create"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project"],
    },
  },
  {
    name: "patch_custom_role",
    description:
      "Update a custom role. Body is a Role resource: { title?: string, description?: string, includedPermissions?: string[], stage?: string, etag?: string }.",
    // Can grant additional access to everything the role is already bound to.
    iamPermissions: ["iam.roles.update"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        role: {
          type: "string",
          description:
            "Custom role ID, the last path segment (e.g. myCustomRole)",
        },
        updateMask: {
          type: "string",
          description: "Comma-separated field mask of fields to update",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "role"],
    },
  },
  {
    name: "delete_custom_role",
    description: "Delete a custom role.",
    iamPermissions: ["iam.roles.delete"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        role: {
          type: "string",
          description:
            "Custom role ID, the last path segment (e.g. myCustomRole)",
        },
        etag: {
          type: "string",
          description: "Etag for consistent read-modify-write",
        },
      },
      required: ["project", "role"],
    },
  },
  {
    name: "undelete_custom_role",
    description:
      "Restore a recently deleted custom role. Body (UndeleteRoleRequest): { etag?: string }.",
    iamPermissions: ["iam.roles.undelete"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        role: {
          type: "string",
          description:
            "Custom role ID, the last path segment (e.g. myCustomRole)",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project", "role"],
    },
  },
  {
    name: "get_predefined_role",
    description: "Get a predefined IAM role (e.g. editor, compute.admin).",
    // Predefined-role metadata is public; GCP requires no permission to
    // read it (unlike get_custom_role, which is project-scoped).
    iamPermissions: [],
    inputSchema: {
      type: "object",
      properties: {
        role: {
          type: "string",
          description:
            "Predefined role ID, without the roles/ prefix, which is added automatically (e.g. editor, compute.admin)",
        },
      },
      required: ["role"],
    },
  },
  {
    name: "query_grantable_roles",
    description:
      "List roles grantable on a resource, given its full resource name.",
    // Introspection-only: GCP requires no specific permission to call this.
    iamPermissions: [],
    inputSchema: {
      type: "object",
      properties: {
        body: {
          type: "object",
          description: "QueryGrantableRolesRequest body.",
          properties: {
            fullResourceName: {
              type: "string",
              description:
                "Full resource name to query grantable roles for (e.g. //cloudresourcemanager.googleapis.com/projects/my-project)",
            },
            view: {
              type: "string",
              description: "BASIC (default, omits includedPermissions) or FULL",
              enum: ["BASIC", "FULL"],
            },
            pageSize: { type: "integer", description: "Max results per page" },
            pageToken: {
              type: "string",
              description: "Page token from a previous response",
            },
          },
          required: ["fullResourceName"],
          additionalProperties: false,
        },
      },
      required: ["body"],
    },
  },
  {
    name: "get_project_iam_policy",
    description:
      "Get the IAM policy for a project. Body (GetIamPolicyRequest): { options?: { requestedPolicyVersion?: number } }.",
    iamPermissions: ["resourcemanager.projects.getIamPolicy"],
    inputSchema: {
      type: "object",
      properties: {
        project: {
          type: "string",
          description: "GCP project ID",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the IAM API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["project"],
    },
  },
];
